import { GroupNodeLabel } from "@features/graph/graphViewModel"
import {
  graphSampleNodeToViewGroupNodeLabel,
  graphViewGroupNodeLabelToSampleNode,
} from "@features/graph/utils/graphUtils"
import { ScatterPlot } from "@features/results/Result/ChartsContainer/ScatterPlot"
import { DashboardChartContainer } from "@features/results/Result/Dashboard/DashboardChartContainer"
import {
  ChartGroupLayout,
  DashboardScatterPlotChartDto,
} from "@features/results/Result/Dashboard/dashboardModel"
import { calculateContainerLayoutDashboardHeight } from "@features/results/Result/Dashboard/layout"
import { CircularProgress, SxProps } from "@mui/material"
import { RunDto } from "@custom-types/modelRun"
import { useGetRunsNodesResultsHistoricQuery } from "@store/services/runService"
import { FunctionComponent, useMemo, useRef } from "react"
import { useDashboardConfig } from "@features/results/Result/Dashboard/dashboardConfig"
import { convertHistoricToRunResults } from "@features/results/Result/runResult.utils"
import { calculateLagShift } from "@utils/run"
import { useIntersectionObserver } from "@hooks/useIntersectionObserver"

interface Props {
  chart: DashboardScatterPlotChartDto
  runs: RunDto[]
  layout?: ChartGroupLayout
}
// normally it should work all cases with height of "100%", but once chart container's height is limited
// by responsive value then chart starts to expand
// https://www.chartjs.org/docs/2.9.4/general/responsive.html
const getContainerSx = (
  chart: DashboardScatterPlotChartDto,
  layout?: ChartGroupLayout,
): SxProps => {
  const titleHeight = 42 // TODO kk: handle multi-rows title
  const selectTimestepHeight = 56

  if (!layout)
    return { height: `calc(100% - ${titleHeight}px - ${selectTimestepHeight}px)`, minHeight: 275 }

  const height = calculateContainerLayoutDashboardHeight(chart, layout)
  if (!height) return {}

  return { height: `calc(${height}vh - ${titleHeight}px - ${selectTimestepHeight}px)` }
}

export const DashboardScatterPlotChart: FunctionComponent<Props> = (props) => {
  const [cfg, updateCfg] = useDashboardConfig({
    showLegend: true,
    selectedNodes: props.chart.Nodes || props.chart.NodeSelect?.[0]?.Nodes || [],
    hiddenDatasets: [],
  })

  const containerRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(containerRef)

  const selectedNodes = cfg.selectedNodes ?? []

  const handleShowLegend = () => {
    updateCfg((draft) => {
      draft.showLegend = !draft.showLegend
    })
  }

  const handleOptionChange = (nodes: string[]) => {
    updateCfg((draft) => {
      draft.selectedNodes = nodes
    })
  }

  const handleLegendClick = (datasetLabel: string) => {
    updateCfg((draft) => {
      const hiddenDatasets = draft.hiddenDatasets ?? []
      if (hiddenDatasets.includes(datasetLabel)) {
        draft.hiddenDatasets = hiddenDatasets.filter((label) => label !== datasetLabel)
      } else {
        draft.hiddenDatasets = [...hiddenDatasets, datasetLabel]
      }
    })
  }

  const handleFullscreen = () => {
    updateCfg((draft) => {
      draft.isFullscreen = !draft.isFullscreen
    })
  }

  const runIds = useMemo(() => props.runs.map((run) => run.key), [props.runs])

  const { isFetching, data: historicResponse } = useGetRunsNodesResultsHistoricQuery(
    {
      runKeys: runIds,
      nodes: selectedNodes.map(graphViewGroupNodeLabelToSampleNode),
    },
    {
      skip: selectedNodes.length === 0 || !isVisible,
    },
  )

  const runResults = useMemo(
    () => convertHistoricToRunResults(historicResponse?.results),
    [historicResponse?.results],
  )
  const lagShift = useMemo(
    () => calculateLagShift(historicResponse?.results),
    [historicResponse?.results],
  )

  const nodeLabels: GroupNodeLabel[] = selectedNodes.map(graphSampleNodeToViewGroupNodeLabel)

  return (
    <DashboardChartContainer
      chart={props.chart}
      layout={props.layout}
      onOptionChange={(v) => handleOptionChange(v.Nodes)}
      onFullScreen={handleFullscreen}
      runIds={runIds}
      onShowLegendToggle={handleShowLegend}
      showLegend={cfg.showLegend}
      ref={containerRef}
    >
      {isFetching ? (
        <div
          style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}
        >
          <CircularProgress />
        </div>
      ) : (
        isVisible && (
          <ScatterPlot
            runs={props.runs}
            rawResultDataFilterNodes={runResults ?? {}}
            lagShift={lagShift}
            // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
            node={nodeLabels[0]}
            onlyChart
            maintainAspectRatio={false}
            chartContainerSx={{ height: "100%", position: "relative" }}
            sx={
              cfg.isFullscreen
                ? { height: "calc(100% - 50px)", width: "100%" }
                : getContainerSx(props.chart, props.layout)
            }
            legendPosition={props.chart.LegendPosition}
            showLegend={cfg.showLegend}
            hiddenDatasets={cfg.hiddenDatasets}
            onLegendClick={handleLegendClick}
          />
        )
      )}
    </DashboardChartContainer>
  )
}
